import { Button } from '@1trading/react/components/ui/button';
import { Card, CardContent } from '@1trading/react/components/ui/card';
import { Checkbox, CheckboxCard } from '@1trading/react/components/ui/checkbox';
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { BINARY_ANSWERS } from '@/constants/answers';
import { MANAGEMENT_ROLES, RegulatedMarketListing } from '@/constants/institution';
import { InstitutionFieldsetPerson } from '@/features/institution/components/fieldsets/person';
import { InstitutionPersonDetailsDifferNotice } from '@/features/institution/components/person-details-differ-notice';
import { type InstitutionManagementPerson, type InstitutionOwnership, type InstitutionOwnershipOrDefaults, isListedOwnership } from '@/features/institution/ownership/schema';
import { createInstitutionPerson, withSharedPersonDetailsOf } from '@/features/institution/schemas/person';
import { reportTaxResidenceFormatErrors } from '@/features/tax-residency/report';
import { useFormFieldArray } from '@/hooks/use-form-field-array';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const MANAGEMENT_MAX = 20;

const createInstitutionManagementPerson = (): InstitutionManagementPerson => ({ ...createInstitutionPerson(), roles: [] });

const InstitutionOwnershipFieldsetManagementLegend = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { t } = useTranslation();
  const isListed = isListedOwnership(form.watch('regulatedMarketListing'));

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.ownership.management.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.ownership.management.description', { context: isListed ? 'listed' : undefined })}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionOwnershipFieldsetManagementLegend.displayName = 'InstitutionOwnershipFieldsetManagementLegend';

const InstitutionOwnershipFieldsetManagement = () => {
  const form = useFormContext<InstitutionOwnershipOrDefaults>();
  const { fields, append, remove, update } = useFormFieldArray({ control: form.control, name: 'management' });
  const { t } = useTranslation();
  const regulatedMarketListing = form.watch('regulatedMarketListing');
  const ubos = form.watch('ubos') ?? [];
  const hasNoOwners = form.watch('hasOwners') === 'NO';
  const addFromKey = hasNoOwners ? 'addFromSeniorManagingOfficial' : 'addFromUbos';
  // A partyId never changes once added, so the field array's snapshot avoids re-rendering on every keystroke
  const managementPartyIds = new Set(fields.map(({ partyId }) => partyId));
  // A listed company names no UBOs, so any left from an earlier answer aren't offered or compared
  const namedUbos = regulatedMarketListing === RegulatedMarketListing.NONE ? ubos : [];
  const ubosToCopy = namedUbos.filter((ubo) => ubo.firstName && !managementPartyIds.has(ubo.partyId));
  const canAddPerson = fields.length < MANAGEMENT_MAX;

  // The copy keeps the owner's partyId, which marks the same person across sections
  const addPersonFromUbo = (partyId: string) => {
    const ubo = ubos.find((candidate) => candidate.partyId === partyId);

    if (!ubo) return;

    const { ownershipPct, ...person } = structuredClone(ubo);

    append({ ...person, roles: [] }, { shouldFocus: false });
  };

  return (
    <FormField
      control={form.control}
      name="management"
      render={({ fieldState: { error } }) => (
        <FormItem>
          <div className="space-y-6">
            {fields.map((person, index) => (
              <Card key={person.id} className="shadow-none" bordered>
                <CardContent className="pt-6">
                  <fieldset className="relative space-y-6">
                    <legend className="font-medium">
                      {t('institutionDetails.ownership.management.person')} {index + 1}
                    </legend>
                    <Button
                      className="absolute top-[-0.66rem] right-[-0.66rem] !m-0 text-destructive hover:text-destructive"
                      size="icon"
                      type="button"
                      variant="ghost"
                      onClick={() => remove(index)}
                    >
                      <Trash2 className="size-4" />
                      <span className="sr-only">
                        {t('institutionDetails.ownership.management.remove')} {index + 1}
                      </span>
                    </Button>
                    <InstitutionPersonDetailsDifferNotice<InstitutionManagementPerson>
                      name={`management.${index}`}
                      source={namedUbos.find(({ partyId }) => partyId === person.partyId)}
                      section={hasNoOwners ? 'seniorManagingOfficial' : 'ubos'}
                      onUseSource={(copy, source) => update(index, withSharedPersonDetailsOf(copy, source))}
                    />
                    <InstitutionFieldsetPerson<InstitutionOwnershipOrDefaults> name={`management.${index}`} />
                    <FormField
                      control={form.control}
                      name={`management.${index}.isPep`}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t('institutionDetails.ownership.management.isPep')}*</FormLabel>
                          <FormDescription>{t('institutionDetails.person.isPepDescription')}</FormDescription>
                          <FormControl>
                            <RadioGroup
                              className="grid-cols-2"
                              defaultValue={field.value}
                              variant="cards"
                              aria-label={t('institutionDetails.ownership.management.isPep')}
                              onValueChange={field.onChange}
                            >
                              {BINARY_ANSWERS.map((answer) => (
                                <FormItem key={answer}>
                                  <RadioGroupItemCard variant="outline">
                                    <FormControl>
                                      <RadioGroupItem value={answer} />
                                    </FormControl>
                                    <FormLabel contained>{t(`fundamentals.${answer.toLowerCase()}`)}</FormLabel>
                                  </RadioGroupItemCard>
                                </FormItem>
                              ))}
                            </RadioGroup>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`management.${index}.roles`}
                      render={() => (
                        <FormItem>
                          <FormLabel>{t('institutionDetails.ownership.management.roles')}*</FormLabel>
                          <FormControl aria-label={t('institutionDetails.ownership.management.roles')}>
                            {/* biome-ignore lint/a11y/useSemanticElements: Makeshift checkboxgroup since it's not a native role */}
                            <div className="space-y-4" role="group">
                              {MANAGEMENT_ROLES.map((role) => (
                                <FormField
                                  key={role}
                                  control={form.control}
                                  name={`management.${index}.roles`}
                                  render={({ field }) => (
                                    <FormItem>
                                      <CheckboxCard variant="outline">
                                        <FormControl>
                                          <Checkbox
                                            checked={field.value?.includes(role)}
                                            variant="cards"
                                            onCheckedChange={(checked) => {
                                              field.onChange(checked ? [...(field.value ?? []), role] : field.value?.filter((value) => value !== role));

                                              // The list rules read every person's roles, so an error they already show is rechecked too
                                              if (form.getFieldState('management').error) form.trigger('management');
                                            }}
                                          />
                                        </FormControl>
                                        <FormLabel contained>{t(`institutionDetails.ownership.management.roleOptions.${role}`)}</FormLabel>
                                      </CheckboxCard>
                                    </FormItem>
                                  )}
                                />
                              ))}
                            </div>
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </fieldset>
                </CardContent>
              </Card>
            ))}
            {canAddPerson ? (
              <div className="flex flex-wrap gap-2">
                {ubosToCopy.length ? (
                  <Select value="" onValueChange={addPersonFromUbo}>
                    <SelectTrigger className="h-9 w-auto gap-2" aria-label={t(`institutionDetails.ownership.management.${addFromKey}`)}>
                      <SelectValue placeholder={t(`institutionDetails.ownership.management.${addFromKey}`)} />
                    </SelectTrigger>
                    <SelectContent>
                      {ubosToCopy.map((ubo) => (
                        <SelectItem key={ubo.partyId} value={ubo.partyId}>
                          {ubo.firstName} {ubo.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : null}
                <Button className="gap-2" size="sm" type="button" variant="outline" onClick={() => append(createInstitutionManagementPerson())}>
                  <Plus className="size-4" />
                  {t('institutionDetails.ownership.management.add')}
                </Button>
              </div>
            ) : null}
          </div>
          {error?.root?.message || error?.message ? <FormMessage>{error.root?.message ?? error.message}</FormMessage> : null}
        </FormItem>
      )}
    />
  );
};
InstitutionOwnershipFieldsetManagement.displayName = 'InstitutionOwnershipFieldsetManagement';

const beforeStepLeave: FormStepGuard<InstitutionOwnership> = async ({ direction, form }, next) => {
  if (direction === 'next') await reportTaxResidenceFormatErrors(form.getValues('management').flatMap(({ taxResidences }) => taxResidences));

  return next();
};

InstitutionOwnershipFieldsetManagement.beforeStepLeave = beforeStepLeave;

export { InstitutionOwnershipFieldsetManagement, InstitutionOwnershipFieldsetManagementLegend };
